import React, { useState } from 'react';

import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import TextField, { TextFieldProps } from '@mui/material/TextField';
import Typography, { TypographyProps } from '@mui/material/Typography';

type EditableLabelProps = {
  value: string;
  onChange: (newValue: string) => void;
  variant?: TypographyProps['variant'];
  typographyProps?: Omit<TypographyProps, 'variant' | 'children'>;
  textFieldProps?: Omit<TextFieldProps, 'value' | 'onChange' | 'autoFocus'>;
};

const EditableLabel: React.FC<EditableLabelProps> = ({
  value,
  onChange,
  variant = 'body1',
  typographyProps,
  textFieldProps,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [draftValue, setDraftValue] = useState(value);

  const handleStartEdit = () => {
    setDraftValue(value);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setDraftValue(value);
    setIsEditing(false);
  };

  const handleConfirm = () => {
    onChange(draftValue);
    setIsEditing(false);
  };

  return (
    <Box display="flex" alignItems="center">
      {isEditing ? (
        <>
          <TextField
            {...textFieldProps}
            value={draftValue}
            onChange={e => setDraftValue(e.target.value)}
            size="small"
            variant="outlined"
            autoFocus
            sx={{ mr: 1, ...textFieldProps?.sx }}
          />
          <IconButton size="small" onClick={handleConfirm} aria-label="Confirm edit">
            <CheckIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={handleCancel} aria-label="Cancel edit">
            <CloseIcon fontSize="small" />
          </IconButton>
        </>
      ) : (
        <>
          <Typography
            variant={variant}
            sx={{ mr: 1, whiteSpace: 'pre-wrap', ...typographyProps?.sx }}
            {...typographyProps}
          >
            {value}
          </Typography>
          <IconButton size="small" onClick={handleStartEdit} aria-label="Edit label">
            <EditIcon fontSize="small" />
          </IconButton>
        </>
      )}
    </Box>
  );
};

export default EditableLabel;
